/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import styles from './CreditGaugeCard.module.scss';

import { CREDIT_LIMIT, CREDIT_ROWS, CREDIT_SCORE } from './CreditGaugeCard.types';
import type { CreditGaugeCardProps } from './CreditGaugeCard.types';

const GAUGE_PATH = 'M20 100 A80 80 0 0 1 180 100';
const COUNT_MS = 1300;

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

export const CreditGaugeCard = ({ className }: CreditGaugeCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useIntersection(ref, { continuous: true, threshold: 0.35 });

  const [reduced, setReduced] = useState(false);
  const [score, setScore] = useState(0);
  const [limit, setLimit] = useState(0);

  useEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  const play = inView && !reduced;

  useEffect(() => {
    if (reduced) {
      setScore(CREDIT_SCORE);
      setLimit(CREDIT_LIMIT);

      return;
    }

    if (!inView) {
      setScore(0);
      setLimit(0);

      return;
    }

    let raf = 0;
    let start = 0;

    const tick = (now: number) => {
      if (!start) start = now;

      const t = Math.min((now - start) / COUNT_MS, 1);
      const eased = easeOut(t);

      setScore(Math.round(eased * CREDIT_SCORE));
      setLimit(Math.round(eased * CREDIT_LIMIT));

      if (t < 1) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, [inView, reduced]);

  return (
    <div ref={ref} className={cn(styles.card, play && styles.play, className)}>
      <div className={styles.head}>
        <span className={styles.dot} />
        <span className={styles.headTitle}>Credit Report — Acme Holdings GmbH</span>
      </div>

      <div className={styles.body}>
        <div className={styles.gauge}>
          <svg
            className={styles.gaugeSvg}
            viewBox="0 0 200 110"
            role="img"
            aria-label={`Credit score ${CREDIT_SCORE} of 100`}
          >
            <path className={styles.arcBg} d={GAUGE_PATH} />
            <path
              className={styles.arcFg}
              d={GAUGE_PATH}
              pathLength={100}
              style={{ strokeDashoffset: 100 - (play ? CREDIT_SCORE : 0) }}
            />
          </svg>

          <div className={styles.gaugeCenter}>
            <span className={styles.score}>{score}</span>
            <span className={styles.scoreMax}>/ 100</span>
          </div>

          <span className={styles.pill}>Low risk</span>
        </div>

        <dl className={styles.rows}>
          <div className={styles.row}>
            <dt className={styles.rowKey}>Recommended credit limit</dt>
            <dd className={cn(styles.rowVal, styles.rowNum)}>€{limit.toLocaleString('en-US')}</dd>
          </div>

          {CREDIT_ROWS.map((r) => (
            <div key={r.label} className={styles.row}>
              <dt className={styles.rowKey}>{r.label}</dt>
              <dd className={cn(styles.rowVal, r.success && styles.rowValOk)}>{r.value}</dd>
            </div>
          ))}
        </dl>

        <div className={styles.stamp}>Score built on filed financials · updated daily</div>
      </div>
    </div>
  );
};
